import { useState } from "react";
import React, { useCallback, useEffect, useRef } from "react";

import Box from "@mui/material/Box";
import { Global } from "@emotion/react";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";

import { Step } from "../types";
import styles from "./FormStyles";
import { GlobalLoader, Loader } from "../Loader";
import { sleep } from "../../utils/miscellaneous";
import { applyMasks } from "../../utils/maskHelper";
import { useAppContext } from "../../contexts/AppContext";
import { trackInteraction } from "../../api/interactionBuffer";
import { resolveQuestionnaire } from "./resolveQuestionnaire";
import { useAppErrorContext } from "../../contexts/AppErrorContext";
import { usePatientDataContext } from "../../contexts/PatientDataContext";
import { usePersonalLinkContext } from "../../contexts/PersonalLinkContext";
import { ConfirmationDialog } from "../ConfirmationDialog/ConfirmationDialog";
import { useQuestionnaireDraftContext } from "../../contexts/QuestionnaireDraftContext";
import {
  addEmergencyContactFieldListener,
  getQuestionnaireStorageKey,
  shapeFormWithDraftData,
  shapeLoginData,
} from "./Form.helpers";

import "lforms/dist/lforms/webcomponent/main.js";
import "lforms/dist/lforms/webcomponent/runtime.js";
import "lforms/dist/lforms/webcomponent/styles.css";
import "lforms/dist/lforms/webcomponent/polyfills.js";
import "lforms/dist/lforms/fhir/R4/lformsFHIR.min.js";
import "lforms/dist/lforms/webcomponent/assets/lib/zone.min.js";

import "./Form.css";

// Placeholder declaration for LForms
declare const LForms: any;

interface FormProps {
  step: Step.NEW_PROFILE_FORM | Step.QUESTIONNAIRE_FORM;
  logger?: string;
  onSubmit: (data: any) => void;
  onInitialization: (data: any) => Promise<void>;
}

const Form: React.FC<FormProps> = ({
  step,
  logger,
  onSubmit,
  onInitialization,
}) => {
  const { isMobile } = useAppContext();

  const [loadingState, setLoadingState] = useState<
    "idle" | "submitting" | "drafting" | "loading"
  >("loading");

  const [isDraftConfirmOpen, setIsDraftConfirmOpen] = useState(false);

  const { openErrorDialog } = useAppErrorContext();

  const [formDef, setFormDef] = useState<any>(null);
  const [formLoadError, setFormLoadError] = useState<boolean>(false);
  const formSourceSubmitRef = useRef<boolean>(step === Step.QUESTIONNAIRE_FORM);

  const { questionnaireDraftRef } = useQuestionnaireDraftContext();
  const { refProviderId, attorneyId } = usePersonalLinkContext();
  const { refParams } = usePersonalLinkContext();
  const {
    sessionId,
    verificationCodes,
    patientId,
    phoneNumber,
    contractId,
    setContractId,
    contract,
  } = usePatientDataContext();

  const formElement = useRef<HTMLDivElement | null>(null);
  const htmlElement = useRef<HTMLDivElement | null>(null);

  const setReadonlyFieldsForSpecificForm = useCallback(
    (formContainer: HTMLDivElement) => {
      let readonlyFields: string[] = [];
      if (step === Step.NEW_PROFILE_FORM) {
        readonlyFields = [
          '[id="6300713197746/1/1"]',
          '[id="19023226162/1/1"]',
          '[id="2036554176854/1/1"]',
          // '[id="1219063476562/1/1"]',
          '[id="9780511808549/1/1"]',
          // '[aria-label="Date of Birth"]',
        ];
      }

      if (step === Step.QUESTIONNAIRE_FORM) {
        if (attorneyId) {
          readonlyFields = [
            '[id="4851389320111/1/1"]',
            '[id="2775401293975/1/1"]',
          ];
        }
        if (refProviderId) {
          readonlyFields.push('[id="5208127706158/1/1"]');
        }
      }
      setReadOnly(formContainer, readonlyFields);
    },
    [attorneyId, refProviderId, step],
  );

  const initLForm = useCallback(
    async (formContainer: HTMLDivElement) => {
      try {
        setFormLoadError(false);
        const formDef = await resolveQuestionnaire(step, contract.type);

        if (!formDef || Object.keys(formDef).length === 0) {
          setFormLoadError(true);
          return;
        }

        setFormDef(formDef);

        const formWithDraftData = shapeFormWithDraftData(
          formDef,
          questionnaireDraftRef.current,
        );
        const options = {
          displayScoreWithAnswerText: false,
        };
        LForms.Util.addFormToPage(formWithDraftData, formContainer, options);

        await sleep(500);
        setReadonlyFieldsForSpecificForm(formContainer);
        applyMasks(formContainer);

        await sleep(500);
        addEmergencyContactFieldListener(formContainer);
      } catch (error) {
        console.error(error);
      }
    },
    [
      step,
      contract.type,
      questionnaireDraftRef,
      setReadonlyFieldsForSpecificForm,
    ],
  );

  const handleRetryLoadForm = async () => {
    setLoadingState("loading");

    const storageKey = getQuestionnaireStorageKey(contract.type);
    localStorage.removeItem(storageKey);

    if (formElement.current) {
      await initLForm(formElement.current);
    }

    setLoadingState("idle");
  };

  useEffect(() => {
    const initializeForm = async () => {
      const loginData = shapeLoginData(
        step,
        sessionId,
        verificationCodes,
        patientId,
        phoneNumber,
        refParams,
      );

      await onInitialization(loginData);

      !!formElement.current && (await initLForm(formElement.current));

      setLoadingState("idle");
    };

    initializeForm();
  }, [
    step,
    initLForm,
    onInitialization,
    patientId,
    phoneNumber,
    refParams,
    sessionId,
    verificationCodes,
  ]);

  const sendFormChangeEvent = useCallback(async () => {
    const questionnaireResponse = LForms.Util.getFormFHIRData(
      "QuestionnaireResponse",
      "R4",
      formElement.current,
      {},
    );

    const payload = {
      patientId,
      contractId,
      device: {
        userAgent: navigator.userAgent,
        userAgentData: (navigator as any).userAgentData ?? null,
        platform: navigator.platform,
        memory: (navigator as any).deviceMemory ?? null,
        connection: (navigator as any).connection ?? null,
        screen: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      },
      questionnaireResponse,
    };

    if (!logger) return;

    await fetch(`${logger}`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }, [patientId, contractId, logger]);

  useEffect(() => {
    if (!formElement.current) return;

    const container = formElement.current;

    let timeoutId: number | undefined;

    const observer = new MutationObserver(() => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }

      timeoutId = window.setTimeout(async () => {
        trackInteraction("form_mutation", step);

        try {
          await sendFormChangeEvent();
        } catch (err) {
          console.error(err);
        }
      }, 2000);
    });

    observer.observe(container, {
      subtree: true,
      childList: true,
      characterData: true,
    });

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
      observer.disconnect();
    };
  }, [step, sendFormChangeEvent]);

  const submitForm = (event: React.FormEvent) => {
    event.preventDefault();

    const validation = LForms.Util.checkValidity(formElement.current);

    if (checkIsValidationNeeded(validation, event)) {
      openErrorDialog(validation[0]);
      return;
    }

    setLoadingState(() => {
      if (event.type === "click") return "drafting";
      return "submitting";
    });

    const submitFormWithLForms = async () => {
      try {
        const options = {};
        const questionnaireResponse = LForms.Util.getFormFHIRData(
          "QuestionnaireResponse",
          "R4",
          formElement.current,
          options,
        );

        if (event.type === "click") {
          questionnaireResponse.status = "draft";
        }

        if (formSourceSubmitRef.current) {
          let all_css = "";
          let styles = document.querySelectorAll("style");

          styles.forEach((style: any, index: number) => {
            all_css += style.innerHTML;
          });

          const htmlBlock = htmlElement.current;

          if (htmlBlock) {
            let formSource = htmlBlock.outerHTML;

            //remove all <!---->
            formSource = formSource.replace(/<!---->/g, "");
            formSource = formSource.replace(/ _ngcontent-rtl-c\d+=""/gm, "");
            formSource = formSource.replace(/ _nghost-rtl-c\d+=""/gm, "");
            formSource = formSource.replace(/ _ngcontent-bsn-c\d+=""/gm, "");

            //append css
            formSource = `<style>${all_css}</style>${formSource}`;

            let formSourceEncodedString = btoa(encodeURIComponent(formSource));

            if (event.type === "click") {
              questionnaireResponse.status = "draft";
            }

            await onSubmit({
              step,
              questionnaireResponse,
              formSource: formSourceEncodedString,
              contractId: contractId,
            });
            setContractId(undefined);
          }
        } else {
          await onSubmit({
            step,
            questionnaireResponse,
            contractId: contractId,
          });
          setContractId(undefined);
        }
      } catch (error) {
        console.error(error);
        setLoadingState("idle");
      }
    };

    if (formSourceSubmitRef.current) {
      buildHtml(submitFormWithLForms);
    } else {
      submitFormWithLForms();
    }
  };

  const buildHtml = (submitFunc: any) => {
    const questionnaireResponse = LForms.Util.getFormFHIRData(
      "QuestionnaireResponse",
      "R4",
      formElement.current,
      {},
    );
    let formWithUserData = null;
    let lformsQ = LForms.Util.convertFHIRQuestionnaireToLForms(formDef, "R4");
    let options = {
      displayScoreWithAnswerText: false,
    };
    if (questionnaireResponse) {
      formWithUserData = LForms.Util.mergeFHIRDataIntoLForms(
        questionnaireResponse,
        lformsQ,
        "R4",
      );
      LForms.Util.addFormToPage(formWithUserData, htmlElement.current, options);
    } else {
      LForms.Util.addFormToPage(lformsQ, htmlElement.current, options);
    }

    setTimeout(() => {
      const htmlBlock = htmlElement.current;

      if (htmlBlock) {
        manipulateDifferentFormHTMLElements(htmlBlock);
        submitFunc();
      }
    }, 1000);
  };

  return (
    <>
      <Paper
        elevation={3}
        style={{
          padding: isMobile ? "10px" : "20px",
        }}
      >
        <>
          {loadingState === "loading" && <GlobalLoader />}
          <Box
            sx={
              loadingState === "loading"
                ? { visibility: "hidden", position: "absolute" }
                : { visibility: "visible" }
            }
          >
            <Global styles={styles.pickerInputHover} />
            <Global styles={styles.pickerReadonly} />

            <div ref={formElement} id="formContainer"></div>

            {formLoadError && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap={2}
                py={4}
              >
                <FormHelperText error sx={{ fontSize: "1rem" }}>
                  Unable to load the questionnaire form. Please try again.
                </FormHelperText>

                <Button
                  variant="contained"
                  onClick={handleRetryLoadForm}
                  disabled={loadingState === "loading"}
                >
                  Retry
                  {loadingState === "loading" && <Loader />}
                </Button>
              </Box>
            )}

            <FormHelperText>
              By submitting this form you consent to receive SMS messages for
              appointment reminders from California Back and Pain Specialists.
              These messages may include appointment details, scheduling
              information, and other relevant updates. Message and data rates
              may apply. You can unsubscribe at any time by contacting us or
              replying STOP to any message.
            </FormHelperText>

            <form onSubmit={submitForm}>
              <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                {step === Step.QUESTIONNAIRE_FORM && (
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={loadingState !== "idle" || formLoadError}
                    onClick={() => setIsDraftConfirmOpen(true)}
                  >
                    Save draft and proceed later
                    {loadingState === "drafting" && <Loader />}
                  </Button>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  disabled={loadingState !== "idle" || formLoadError}
                >
                  {step === Step.QUESTIONNAIRE_FORM
                    ? "Finalize and Submit"
                    : "Submit"}
                  {loadingState === "submitting" && <Loader />}
                </Button>
              </Box>
            </form>

            <div
              style={{
                position: "absolute",
                top: "-10000px",
                left: "-10000px",
              }}
            >
              <div ref={htmlElement} id="htmlContainer"></div>
            </div>
          </Box>
        </>
      </Paper>

      <ConfirmationDialog
        open={isDraftConfirmOpen}
        title="Are you sure you want to save your questionnaire as a draft?"
        message={`Use this option only if you haven't fully completed all fields yet or if you're not ready to submit it.\nYou will need to log in again to submit it.`}
        confirmLabel="Confirm and save draft"
        cancelLabel="Cancel"
        onConfirm={(event) => {
          submitForm(event);
          setIsDraftConfirmOpen(false);
        }}
        onCancel={() => setIsDraftConfirmOpen(false)}
      />
    </>
  );
};

export default Form;

function setReadOnly(formContainer: HTMLDivElement, selectors: string[]) {
  if (!formContainer) {
    return;
  }

  selectors.forEach((selector) => {
    const input = formContainer.querySelector(`${selector}`);
    if (input) {
      input.setAttribute("readonly", "true");
      var elClone = input.cloneNode(true);
      if (input.parentNode && elClone) {
        input.parentNode.replaceChild(elClone, input);
      }
    }
  });
}

function checkIsValidationNeeded(validation: unknown, event: { type: string }) {
  return validation !== null && event.type !== "click";
}

function manipulateDifferentFormHTMLElements(htmlBlock: HTMLDivElement) {
  //get info from each input, and paste it in span before input
  const inputs = htmlBlock.querySelectorAll("input");

  inputs.forEach((input: any) => {
    const span = document.createElement("span");
    span.style.fontWeight = "bold";
    span.innerHTML = input.value;
    input.parentNode.insertBefore(span, input);
    input.style.display = "none";
  });

  const textareas = htmlBlock.querySelectorAll("textarea");
  textareas.forEach((textarea: any) => {
    const span = document.createElement("span");
    span.style.fontWeight = "bold";
    span.innerHTML = textarea.value;
    textarea.parentNode.insertBefore(span, textarea);
    textarea.style.display = "none";
  });

  const datepickers = htmlBlock.querySelectorAll(".lhc-date-picker");
  datepickers.forEach((datepicker: any) => {
    const span = document.createElement("span");
    span.style.fontWeight = "bold";
    span.innerHTML = datepicker.innerText;
    datepicker.parentNode.insertBefore(span, datepicker);
    datepicker.style.display = "none";
  });

  // @ts-ignore
  const autocompleteButtons = htmlBlock.querySelectorAll(
    "lhc-item-choice-autocomplete button",
  );
  //remove autocomplete buttons
  autocompleteButtons.forEach((button: any) => {
    button.style.display = "none";
  });

  const autocompleteSelected = htmlBlock.querySelectorAll(".autocomp_selected");
  //remove class autocomplete-selected
  autocompleteSelected.forEach((selected: any) => {
    selected.classList.replace(
      "autocomp_selected",
      "autocomp_selected_replaced",
    );
    //add('autocomp_selected_replaced').remove('autocomp_selected');
  });

  const popover = htmlBlock.querySelectorAll("[nz-popover]");
  //remove popover
  popover.forEach((popover: any) => {
    popover.style.display = "none";
  });

  const lhcItemMessages = htmlBlock.querySelectorAll(".lhc-item-messages");
  lhcItemMessages.forEach((lhcItemMessage: any) => {
    lhcItemMessage.style.display = "none";
  });

  const lhcValidate = htmlBlock.querySelectorAll(".lhc-validate");
  lhcValidate.forEach((lhcValidate: any) => {
    lhcValidate.style.display = "none";
  });

  // Get all elements inside the specific block
  const elementsInsideBlock = htmlBlock.querySelectorAll("*");

  // Filter the invisible elements
  // @ts-ignore
  const invisibleElements = [...elementsInsideBlock].filter((element) => {
    const styles = getComputedStyle(element);
    return styles.display === "none" || styles.visibility === "hidden";
  });

  // Remove the invisible elements
  invisibleElements.forEach((element) => {
    element.parentNode?.removeChild(element);
  });
}
